import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";

import {
    FaEdit,
    FaTrash,
    FaPlus,
    FaTimes,
    FaYoutube,
    FaExternalLinkAlt
} from "react-icons/fa";

import API_BASE_URL from "../config/api";
import QuillEditor from "../components/QuillEditor";

const YoutubeVideos = () => {

    // =========================================================
    // STATES
    // =========================================================

    const [videos, setVideos] = useState([]);

    const [loading, setLoading] = useState(false);

    const [saving, setSaving] = useState(false);

    const [showForm, setShowForm] = useState(false);

    const [editId, setEditId] = useState(null);

    const [formData, setFormData] = useState({
        title: "",
        author: "",
        description: "",
        video_link: "",
        sort_order: "1"
    });


    // =========================================================
    // FETCH YOUTUBE VIDEOS
    // =========================================================

    const fetchVideos = async () => {

        try {

            setLoading(true);

            const response = await fetch(
                `${API_BASE_URL}/admin/youtube-videos`,
                {
                    method: "GET",

                    headers: {
                        Accept: "application/json"
                    },

                    credentials: "include"
                }
            );

            const data = await response.json();

            console.log(
                "YOUTUBE VIDEO LIST RESPONSE:",
                data
            );

            if (response.ok && data.success) {

                setVideos(data.data || []);

            } else {

                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text:
                        data.message ||
                        "Unable to fetch YouTube videos."
                });
            }

        } catch (error) {

            console.error(
                "FETCH YOUTUBE VIDEO ERROR:",
                error
            );

            Swal.fire({
                icon: "error",
                title: "Error",
                text:
                    "Something went wrong while loading YouTube videos."
            });

        } finally {

            setLoading(false);
        }
    };


    // =========================================================
    // USE EFFECT
    // =========================================================

    useEffect(() => {

        fetchVideos();

    }, []);


    // =========================================================
    // INPUT CHANGE
    // =========================================================

    const handleChange = (e) => {

        const {
            name,
            value
        } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));
    };


    // =========================================================
    // RESET FORM
    // =========================================================

    const resetForm = () => {

        setFormData({
            title: "",
            author: "",
            description: "",
            video_link: "",
            sort_order: "1"
        });

        setEditId(null);

        setShowForm(false);
    };


    // =========================================================
    // OPEN ADD FORM
    // =========================================================

    const handleAdd = () => {

        setFormData({
            title: "",
            author: "",
            description: "",
            video_link: "",
            sort_order: "1"
        });

        setEditId(null);

        setShowForm(true);
    };


    // =========================================================
    // EDIT YOUTUBE VIDEO
    // =========================================================

    const handleEdit = async (id) => {

        try {

            setSaving(true);

            const response = await fetch(
                `${API_BASE_URL}/admin/youtube-videos/${id}`,
                {
                    method: "GET",

                    headers: {
                        Accept: "application/json"
                    },

                    credentials: "include"
                }
            );

            const data = await response.json();

            console.log(
                "YOUTUBE VIDEO EDIT RESPONSE:",
                data
            );

            if (!response.ok || !data.success) {

                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text:
                        data.message ||
                        "Unable to fetch YouTube video."
                });

                return;
            }

            const video = data.data;

            setFormData({

                title:
                    video.title || "",

                author:
                    video.author || "",

                description:
                    video.description || "",

                video_link:
                    video.video_link || "",

                sort_order:
                    video.sort_order !== null &&
                    video.sort_order !== undefined
                        ? String(video.sort_order)
                        : "1"
            });

            setEditId(id);

            setShowForm(true);

        } catch (error) {

            console.error(
                "EDIT YOUTUBE VIDEO ERROR:",
                error
            );

            Swal.fire({
                icon: "error",
                title: "Error",
                text:
                    "Something went wrong while loading YouTube video."
            });

        } finally {

            setSaving(false);
        }
    };


    // =========================================================
    // SAVE YOUTUBE VIDEO
    // =========================================================

    const handleSubmit = async (e) => {

        e.preventDefault();


        // =====================================================
        // TITLE VALIDATION
        // =====================================================

        if (!formData.title.trim()) {

            Swal.fire({
                icon: "warning",
                title: "Title Required",
                text:
                    "Please enter YouTube video title."
            });

            return;
        }


        // =====================================================
        // DESCRIPTION VALIDATION
        // =====================================================

        const plainDescription =
            formData.description
                .replace(/<(.|\n)*?>/g, "")
                .trim();

        if (!plainDescription) {

            Swal.fire({
                icon: "warning",
                title: "Description Required",
                text:
                    "Please enter YouTube video description."
            });

            return;
        }


        // =====================================================
        // VIDEO LINK VALIDATION
        // =====================================================

        if (!formData.video_link.trim()) {

            Swal.fire({
                icon: "warning",
                title: "Video Link Required",
                text:
                    "Please enter YouTube video link."
            });

            return;
        }


        // =====================================================
        // SORT ORDER VALIDATION
        // =====================================================

        if (!formData.sort_order.trim()) {

            Swal.fire({
                icon: "warning",
                title: "Sort Order Required",
                text:
                    "Please enter sort order."
            });

            return;
        }


        try {

            setSaving(true);


            // =================================================
            // FORM DATA
            // =================================================

            const data = new FormData();


            data.append(
                "title",
                formData.title.trim()
            );


            // Author optional

            data.append(
                "author",
                formData.author.trim()
            );


            // Quill HTML

            data.append(
                "description",
                formData.description
            );


            data.append(
                "video_link",
                formData.video_link.trim()
            );


            data.append(
                "sort_order",
                formData.sort_order.trim()
            );


            // =================================================
            // URL
            // =================================================

            let url =
                `${API_BASE_URL}/admin/youtube-videos`;

            let method = "POST";


            // =================================================
            // UPDATE
            // =================================================

            if (editId) {

                url =
                    `${API_BASE_URL}/admin/youtube-videos/${editId}`;

                /*
                 * Laravel Method Spoofing
                 *
                 * POST + _method=PUT
                 */

                data.append(
                    "_method",
                    "PUT"
                );
            }


            console.log(
                "YOUTUBE VIDEO FORM DATA:",
                {
                    title: formData.title,
                    author: formData.author,
                    description: formData.description,
                    video_link: formData.video_link,
                    sort_order: formData.sort_order
                }
            );


            // =================================================
            // API REQUEST
            // =================================================

            const response = await fetch(
                url,
                {
                    method: method,

                    headers: {
                        Accept: "application/json"
                    },

                    body: data,

                    credentials: "include"
                }
            );


            const result =
                await response.json();


            console.log(
                "YOUTUBE VIDEO SAVE RESPONSE:",
                result
            );


            // =================================================
            // SUCCESS
            // =================================================

            if (response.ok && result.success) {

                Swal.fire({
                    icon: "success",

                    title:
                        editId
                            ? "YouTube Video Updated"
                            : "YouTube Video Added",

                    text:
                        result.message ||
                        "YouTube video saved successfully.",

                    timer: 1800,

                    showConfirmButton: false
                });


                resetForm();

                fetchVideos();

            } else {

                console.error(
                    "YOUTUBE VIDEO SAVE ERROR RESPONSE:",
                    result
                );


                let errorMessage =
                    result.message ||
                    "Unable to save YouTube video.";


                // =================================================
                // LARAVEL VALIDATION ERRORS
                // =================================================

                if (result.errors) {

                    const validationMessages =
                        Object.values(result.errors)
                            .flat()
                            .join("\n");


                    if (validationMessages) {

                        errorMessage =
                            validationMessages;
                    }
                }


                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: errorMessage
                });
            }

        } catch (error) {

            console.error(
                "SAVE YOUTUBE VIDEO ERROR:",
                error
            );

            Swal.fire({
                icon: "error",
                title: "Error",
                text:
                    "Something went wrong while saving YouTube video."
            });

        } finally {

            setSaving(false);
        }
    };


    // =========================================================
    // DELETE YOUTUBE VIDEO
    // =========================================================

    const handleDelete = async (id) => {

        const confirm =
            await Swal.fire({

                title:
                    "Delete YouTube Video?",

                text:
                    "This YouTube video will be permanently deleted.",

                icon:
                    "warning",

                showCancelButton:
                    true,

                confirmButtonText:
                    "Yes, Delete",

                cancelButtonText:
                    "Cancel"
            });


        if (!confirm.isConfirmed) {

            return;
        }


        try {

            setSaving(true);


            const response =
                await fetch(
                    `${API_BASE_URL}/admin/youtube-videos/${id}`,
                    {
                        method: "DELETE",

                        headers: {
                            Accept: "application/json"
                        },

                        credentials: "include"
                    }
                );


            const result =
                await response.json();


            console.log(
                "DELETE YOUTUBE VIDEO RESPONSE:",
                result
            );


            if (response.ok && result.success) {

                Swal.fire({
                    icon: "success",

                    title:
                        "Deleted",

                    text:
                        result.message ||
                        "YouTube video deleted successfully.",

                    timer:
                        1500,

                    showConfirmButton:
                        false
                });


                fetchVideos();

            } else {

                Swal.fire({
                    icon: "error",

                    title:
                        "Error",

                    text:
                        result.message ||
                        "Unable to delete YouTube video."
                });
            }

        } catch (error) {

            console.error(
                "DELETE YOUTUBE VIDEO ERROR:",
                error
            );

            Swal.fire({
                icon: "error",

                title:
                    "Error",

                text:
                    "Something went wrong while deleting YouTube video."
            });

        } finally {

            setSaving(false);
        }
    };


    // =========================================================
    // YOUTUBE VIDEO ID
    // =========================================================

    const getYoutubeVideoId = (url) => {

        if (!url) {

            return null;
        }


        try {

            const parsedUrl =
                new URL(url);


            // =================================================
            // youtube.com/watch?v=VIDEO_ID
            // =================================================

            if (
                parsedUrl.hostname.includes("youtube.com") &&
                parsedUrl.searchParams.get("v")
            ) {

                return parsedUrl.searchParams.get("v");
            }


            // =================================================
            // youtu.be/VIDEO_ID
            // =================================================

            if (
                parsedUrl.hostname.includes("youtu.be")
            ) {

                return parsedUrl.pathname
                    .replace("/", "")
                    .split("?")[0];
            }


            // =================================================
            // youtube.com/embed/VIDEO_ID
            // =================================================

            if (
                parsedUrl.pathname.includes("/embed/")
            ) {

                return parsedUrl.pathname
                    .split("/embed/")[1]
                    .split("/")[0];
            }


            return null;

        } catch (error) {

            return null;
        }
    };


    // =========================================================
    // YOUTUBE THUMBNAIL
    // =========================================================

    const getYoutubeThumbnail = (url) => {

        const videoId =
            getYoutubeVideoId(url);


        if (!videoId) {

            return null;
        }


        return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
    };


    // =========================================================
    // RENDER
    // =========================================================

    return (

        <div
            className="container-fluid py-4"
            style={{
                maxHeight: "500px",
                overflowX: "auto",
                overflowY: "auto"
            }}
        >

            {/* =================================================
                HEADER
            ================================================= */}

            <div className="d-flex justify-content-between align-items-center mb-4">

                <div>

                    <h4 className="mb-1">
                        YouTube Videos
                    </h4>

                    <p className="text-muted mb-0">
                        Manage website YouTube videos
                    </p>

                </div>


                {!showForm && (

                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleAdd}
                    >

                        <FaPlus className="me-2" />

                        Add YouTube Video

                    </button>

                )}

            </div>


            {/* =================================================
                ADD / EDIT FORM
            ================================================= */}

            {showForm && (

                <div className="card shadow-sm mb-4">

                    <div className="card-header d-flex justify-content-between align-items-center">

                        <h5 className="mb-0">

                            {editId
                                ? "Edit YouTube Video"
                                : "Add YouTube Video"}

                        </h5>


                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={resetForm}
                            disabled={saving}
                        >

                            <FaTimes />

                        </button>

                    </div>


                    <div className="card-body">

                        <form onSubmit={handleSubmit}>

                            <div className="row">


                                {/* =================================================
                                    TITLE
                                ================================================= */}

                                <div className="col-md-6 mb-3">

                                    <label className="form-label">

                                        Title

                                        <span className="text-danger">
                                            *
                                        </span>

                                    </label>


                                    <input
                                        type="text"
                                        name="title"
                                        className="form-control"
                                        value={formData.title}
                                        onChange={handleChange}
                                        maxLength={100}
                                        placeholder="Enter YouTube video title"
                                        disabled={saving}
                                    />

                                </div>


                                {/* =================================================
                                    AUTHOR
                                ================================================= */}

                                <div className="col-md-6 mb-3">

                                    <label className="form-label">

                                        Author

                                        <span className="text-muted ms-1">
                                            (Optional)
                                        </span>

                                    </label>


                                    <input
                                        type="text"
                                        name="author"
                                        className="form-control"
                                        value={formData.author}
                                        onChange={handleChange}
                                        maxLength={50}
                                        placeholder="Enter author name"
                                        disabled={saving}
                                    />

                                </div>


                                {/* =================================================
                                    VIDEO LINK
                                ================================================= */}

                                <div className="col-md-9 mb-3">

                                    <label className="form-label">

                                        YouTube Video Link

                                        <span className="text-danger">
                                            *
                                        </span>

                                    </label>


                                    <input
                                        type="url"
                                        name="video_link"
                                        className="form-control"
                                        value={formData.video_link}
                                        onChange={handleChange}
                                        maxLength={256}
                                        placeholder="https://www.youtube.com/watch?v=XXXXXXXXXXX"
                                        disabled={saving}
                                    />


                                    <small className="text-muted">

                                        Example:
                                        {" "}
                                        https://www.youtube.com/watch?v=XXXXXXXXXXX

                                    </small>

                                </div>


                                {/* =================================================
                                    SORT ORDER
                                ================================================= */}

                                <div className="col-md-3 mb-3">

                                    <label className="form-label">

                                        Sort Order

                                        <span className="text-danger">
                                            *
                                        </span>

                                    </label>


                                    <input
                                        type="number"
                                        name="sort_order"
                                        className="form-control"
                                        value={formData.sort_order}
                                        onChange={handleChange}
                                        min="1"
                                        max="9999"
                                        placeholder="1"
                                        disabled={saving}
                                    />

                                </div>


                                {/* =================================================
                                    VIDEO PREVIEW
                                ================================================= */}

                                {getYoutubeThumbnail(
                                    formData.video_link
                                ) && (

                                    <div className="col-12 mb-4">

                                        <label className="form-label">
                                            Video Preview
                                        </label>


                                        <div>

                                            <img
                                                src={
                                                    getYoutubeThumbnail(
                                                        formData.video_link
                                                    )
                                                }
                                                alt="YouTube Preview"
                                                style={{
                                                    width: "220px",
                                                    height: "125px",
                                                    objectFit: "cover",
                                                    borderRadius: "8px",
                                                    border: "1px solid #ddd"
                                                }}
                                            />

                                        </div>

                                    </div>

                                )}


                                {/* =================================================
                                    DESCRIPTION
                                ================================================= */}

                                <div className="col-12 mb-4">

                                    <label className="form-label">

                                        Description

                                        <span className="text-danger">
                                            *
                                        </span>

                                    </label>


                                    <QuillEditor
                                        value={
                                            formData.description
                                        }

                                        onChange={(value) =>
                                            setFormData((prev) => ({
                                                ...prev,
                                                description: value
                                            }))
                                        }
                                    />

                                </div>


                                {/* =================================================
                                    BUTTONS
                                ================================================= */}

                                <div className="col-12">

                                    <button
                                        type="submit"
                                        className="btn btn-primary me-2"
                                        disabled={saving}
                                    >

                                        {saving
                                            ? "Saving..."
                                            : editId
                                                ? "Update YouTube Video"
                                                : "Add YouTube Video"}

                                    </button>


                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={resetForm}
                                        disabled={saving}
                                    >

                                        Cancel

                                    </button>

                                </div>

                            </div>

                        </form>

                    </div>

                </div>

            )}


            {/* =================================================
                YOUTUBE VIDEO LIST
            ================================================= */}

            {!showForm && (

                <div className="card shadow-sm">

                    <div className="card-body p-0">

                        {loading ? (

                            <div className="text-center py-5">

                                <div
                                    className="spinner-border text-primary"
                                    role="status"
                                >
                                </div>


                                <p className="mt-2 mb-0">
                                    Loading YouTube videos...
                                </p>

                            </div>

                        ) : videos.length === 0 ? (

                            <div className="text-center py-5">

                                <p className="text-muted mb-3">
                                    No YouTube videos found.
                                </p>


                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleAdd}
                                >

                                    <FaPlus className="me-2" />

                                    Add YouTube Video

                                </button>

                            </div>

                        ) : (

                            <div className="table-responsive">

                                <table className="table table-bordered table-hover align-middle mb-0">

                                    <thead className="table-light">

                                        <tr>

                                            <th
                                                style={{
                                                    width: "70px"
                                                }}
                                            >
                                                ID
                                            </th>


                                            <th
                                                style={{
                                                    width: "180px"
                                                }}
                                            >
                                                Video
                                            </th>


                                            <th>
                                                Title
                                            </th>


                                            <th>
                                                Author
                                            </th>


                                            <th>
                                                Description
                                            </th>


                                            <th
                                                style={{
                                                    width: "90px"
                                                }}
                                            >
                                                Order
                                            </th>


                                            <th
                                                style={{
                                                    width: "150px"
                                                }}
                                            >
                                                Action
                                            </th>

                                        </tr>

                                    </thead>


                                    <tbody>

                                        {videos.map((video) => (

                                            <tr
                                                key={video.id}
                                            >

                                                {/* =================================================
                                                    ID
                                                ================================================= */}

                                                <td>
                                                    {video.id}
                                                </td>


                                                {/* =================================================
                                                    VIDEO
                                                ================================================= */}

                                                <td>

                                                    {getYoutubeThumbnail(
                                                        video.video_link
                                                    ) ? (

                                                        <div>

                                                            <img
                                                                src={
                                                                    getYoutubeThumbnail(
                                                                        video.video_link
                                                                    )
                                                                }
                                                                alt={
                                                                    video.title
                                                                }
                                                                style={{
                                                                    width: "150px",
                                                                    height: "85px",
                                                                    objectFit: "cover",
                                                                    borderRadius: "6px",
                                                                    border: "1px solid #ddd"
                                                                }}
                                                            />


                                                            <div className="mt-2">

                                                                <a
                                                                    href={
                                                                        video.video_link
                                                                    }
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="btn btn-sm btn-outline-danger"
                                                                >

                                                                    <FaYoutube className="me-1" />

                                                                    Watch

                                                                    <FaExternalLinkAlt
                                                                        className="ms-1"
                                                                        size={10}
                                                                    />

                                                                </a>

                                                            </div>

                                                        </div>

                                                    ) : (

                                                        <a
                                                            href={
                                                                video.video_link
                                                            }
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="btn btn-sm btn-outline-danger"
                                                        >

                                                            <FaYoutube className="me-1" />

                                                            Open Video

                                                        </a>

                                                    )}

                                                </td>


                                                {/* =================================================
                                                    TITLE
                                                ================================================= */}

                                                <td>

                                                    <strong>
                                                        {video.title}
                                                    </strong>

                                                </td>


                                                {/* =================================================
                                                    AUTHOR
                                                ================================================= */}

                                                <td>

                                                    {video.author
                                                        ? video.author
                                                        : (
                                                            <span className="text-muted">
                                                                Not specified
                                                            </span>
                                                        )}

                                                </td>


                                                {/* =================================================
                                                    DESCRIPTION
                                                ================================================= */}

                                                <td>

                                                    <div
                                                        style={{
                                                            maxWidth: "350px",
                                                            maxHeight: "100px",
                                                            overflow: "hidden"
                                                        }}

                                                        dangerouslySetInnerHTML={{
                                                            __html:
                                                                video.description || ""
                                                        }}
                                                    />

                                                </td>


                                                {/* =================================================
                                                    SORT ORDER
                                                ================================================= */}

                                                <td>
                                                    {video.sort_order}
                                                </td>


                                                {/* =================================================
                                                    ACTION
                                                ================================================= */}

                                                <td>

                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-warning me-2"
                                                        onClick={() =>
                                                            handleEdit(
                                                                video.id
                                                            )
                                                        }
                                                        disabled={saving}
                                                        title="Edit"
                                                    >

                                                        <FaEdit />

                                                    </button>


                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-danger"
                                                        onClick={() =>
                                                            handleDelete(
                                                                video.id
                                                            )
                                                        }
                                                        disabled={saving}
                                                        title="Delete"
                                                    >

                                                        <FaTrash />

                                                    </button>

                                                </td>

                                            </tr>

                                        ))}

                                    </tbody>

                                </table>

                            </div>

                        )}

                    </div>

                </div>

            )}

        </div>
    );
};

export default YoutubeVideos;
